import { PATH } from 'constants/path';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EAppointmentType, EMessageSenderType, EMessageType, TConversation, TMessage } from 'types/inbox';
import { getTimeStr } from 'utils/calendar';
import AvaIcon from 'assets/common/ava.png';
import s from './s.module.less';
import { getCalendarAppointmentEventDetail } from 'api/google';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { Spin } from 'antd';

type Props = {
    inboxConversation?: TConversation,
    inboxMessage?: TMessage,
    type: 'PATIENT' | 'PROVIDER'
};

const InboxMessage = (props: Props) => {
    const { inboxMessage, inboxConversation, type } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const rightToLeft = inboxMessage?.senderType === type;
    const showName = inboxMessage?.senderType !== type;
    let firstName = '';
    let lastName = '';
    if (inboxMessage?.senderType === 'PATIENT') {
        firstName = inboxConversation?.patientFirstName || '';
        lastName = inboxConversation?.patientLastName || '';
    } else if (inboxMessage?.senderType === 'PROVIDER') {
        firstName = inboxConversation?.providerFirstName || '';
        lastName = inboxConversation?.providerLastName || '';
    } else {
        firstName = 'System';
        lastName = '';
    }

    const appointment = inboxMessage?.appointment;
    let appointmentTitle = '';
    let appointmentTips = 'Please review the details and get ready to meet your new patient!';
    let oldTimeVal = '';
    let newTimeVal = '';
    let feeLabel = '';
    if (appointment) {
        if (appointment.type === EAppointmentType.NEW_APPOINTMENT_CREATED) {
            appointmentTitle = '🎊 Congratulations! You have a new appointment:';
        }
        if (appointment.type === EAppointmentType.RESCHEDULED) {
            appointmentTitle = '🔄 Appointment rescheduled:';
            appointmentTips = 'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
            feeLabel = 'Reschedule fee:';
            //Tuesday, March 3, 2025, 8:30 - 9:00 am
            oldTimeVal = `${dayjs(appointment.oldStartAt).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.oldStartAt).add(appointment.duration, 'minutes').format('hh:mm A')}`;
            newTimeVal = `${dayjs(appointment.startAt).format('dddd, MMMM D, YYYY, hh:mm A')} - ${dayjs(appointment.startAt).add(appointment.duration, 'minutes').format('hh:mm A')}`;
        }
        if (appointment.type === EAppointmentType.PATIENT_CANCELED) {
            appointmentTitle = '❌ Appointment canceled:';
            appointmentTips = 'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
        }
        if (appointment.type === EAppointmentType.PROVIDER_CANCELED) {
            appointmentTitle = '🎉 You have successfully cancelled the patient appointment:';
            appointmentTips = '';
        }
        if (appointment.type === EAppointmentType.PATIENT_LATE_CANCELED) {
            appointmentTitle = '❌ Appointment late canceled:';
            appointmentTips = 'You may want to reach out to the client to reschedule or assist with rebooking at a more convenient time.';
            feeLabel = 'Late cancellation fee:';
        }
    }

    const handleClickViewAppointment = async (id: number) => {
        if (!id) {
            return;
        }
        setLoading(true);
        const res = await getCalendarAppointmentEventDetail(`${id}`);
        setLoading(false);
        if (!res.error) {
            const resData: TServerCalendarAppointmentEventDetail | undefined = res?.data?.data || undefined;
            if (resData) {
                window.open(`${PATH.DASHBOARD}/${PATH.CALENDAR}?aid=${resData?.id}&date=${getTimeStr(dayjs(resData?.startAt), 'DD-MM-YYYY')}`, '_blank');
            }
        }
    };

    const getHtmlContent = (content: string, fromProvider?: boolean) => {
        let ret = content.replace(/\n/g, '<br />');
        if (fromProvider) {
            //提取string里面的link，加上<a>标签
            const linkRegex = /(https?:\/\/[^\s"'<>]+[^\s"'<>.,;!?)]*)/g;
            ret = ret.replace(linkRegex, (match) => `<a href="${match}" target="_blank">${match}</a>`);
        }
        return ret;
    };

    return (
        <div className={`${s.wrap} ${rightToLeft ? s.wrapRight : ''}`}>
            {
                inboxMessage?.text && (
                    <div className={s.textItem}>
                        {
                            inboxMessage.createdAt && inboxMessage.showSendTime &&
                            <div className={s.sendTimeWrap}>
                                {
                                    showName ? `${firstName} ${lastName}`.trim() : ''
                                }
                                <span className={s.sendTimeVal}>
                                    {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                                </span>
                            </div>
                        }
                        <div
                            className={s.textWrap}
                            dangerouslySetInnerHTML={
                                {
                                    __html: getHtmlContent(inboxMessage.text.body, inboxMessage.senderType === EMessageSenderType.PROVIDER),
                                }
                            }
                        />
                    </div>
                )
            }
            {
                appointment && (

                    <div className={s.appItem}>
                        <div className={s.sendTimeWrap}>
                            {
                                showName ? `${firstName} ${lastName}`.trim() : ''
                            }
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <Spin spinning={loading} className={s.appSpin} wrapperClassName={s.appSpinWrap}>
                            <div className={s.appWrap}>
                                <div className={s.appTitle}>{appointmentTitle}</div>
                                <ul className={s.appInfoList}>
                                    <li className={s.appName}>{appointment.serviceState ? `[${appointment.serviceState}] ` : ''} {appointment?.appointmentName}</li>
                                    <li className={s.appClient}>
                                        <div className={s.label}>Client:</div>
                                        <div
                                            onClick={() => {
                                                if (appointment.clientId) {
                                                    window.open(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${appointment?.clientId}`, '_blank');
                                                }
                                            }}
                                            className={s.appLink}
                                        >
                                            {`${inboxConversation?.patientFirstName} ${inboxConversation?.patientLastName}`}
                                        </div>
                                    </li>
                                    {
                                        appointment.type !== EAppointmentType.RESCHEDULED && (
                                            <>
                                                <li className={s.appInfoItem}>
                                                    <div className={s.label}>Date:</div>
                                                    <div
                                                        onClick={() => {

                                                        }}
                                                        className={s.value}
                                                    >
                                                        {/* Tuesday, March 3, 2025 */}
                                                        {dayjs(appointment.startAt).format('dddd, MMMM D, YYYY')}
                                                    </div>
                                                </li>
                                                <li className={s.appInfoItem}>
                                                    <div className={s.label}>Time:</div>
                                                    <div
                                                        onClick={() => {

                                                        }}
                                                        className={s.value}
                                                    >
                                                        {`${dayjs(appointment.startAt).format(dayjs(appointment.startAt)?.hour() === 0 ? 'HH:mm' : 'hh:mm')} - ${dayjs(appointment.startAt).add(appointment.duration, 'minutes').format('hh:mm A')} | ${appointment.duration} mins`}
                                                    </div>
                                                </li>
                                            </>
                                        )
                                    }
                                    {
                                        appointment.type === EAppointmentType.RESCHEDULED && (
                                            <>
                                                <li className={s.appInfoItem}>
                                                    <div className={s.label}>Original Time:</div>
                                                    <div
                                                        style={{ textDecoration: 'line-through' }}
                                                        className={s.value}
                                                    >
                                                        {/* Tuesday, March 3, 2025 */}
                                                        {oldTimeVal}
                                                    </div>
                                                </li>
                                                <li className={s.appInfoItem}>
                                                    <div className={s.label}>New Time:</div>
                                                    <div
                                                        className={s.value}
                                                    >
                                                        {newTimeVal}
                                                    </div>
                                                </li>
                                            </>
                                        )
                                    }
                                    {
                                        [EAppointmentType.RESCHEDULED, EAppointmentType.PATIENT_LATE_CANCELED].includes(appointment.type) && (
                                            <li className={s.appInfoItem}>
                                                <div className={s.label}>{feeLabel}</div>
                                                <div
                                                    className={s.value}
                                                >
                                                    {appointment.chargeFee ? `$${appointment.chargeFee}` : 'Not applied based on your billing policy'}
                                                </div>
                                            </li>
                                        )
                                    }

                                </ul>
                                {
                                    appointment.type !== EAppointmentType.RESCHEDULED && (
                                        <div className={s.appTips}>
                                            {appointmentTips}
                                        </div>
                                    )
                                }

                                <div
                                    onClick={() => {
                                        if (appointment.id) {
                                            handleClickViewAppointment(appointment.id);
                                        }
                                    }}
                                    className={s.appLink}
                                >
                                    View appointment
                                </div>
                            </div>
                        </Spin>
                    </div>
                )
            }
            {
                inboxMessage?.timeLabel && (
                    <div className={s.dateWrap}>
                        {inboxMessage.timeLabel}
                    </div>
                )
            }
            {
                inboxMessage?.question && (
                    <div className={s.qsItem}>
                        <div className={s.sendTimeWrap}>
                            {
                                showName ? `${firstName} ${lastName}`.trim() : ''
                            }
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.qsWrap}>
                            <div className={s.subject}>{inboxMessage.question.subject}</div>
                            <div
                                className={s.content}
                                dangerouslySetInnerHTML={{
                                    __html: getHtmlContent(inboxMessage.question.body, inboxMessage.senderType === EMessageSenderType.PROVIDER),
                                }}
                            />
                        </div>
                    </div>
                )
            }
            {
                inboxMessage?.avaReply && (
                    <div className={s.avaItem}>
                        <div className={s.sendTimeWrap}>
                            {/* {
                                showName ? `${firstName} ${lastName}`.trim() : ''
                            } */}
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.avaWrap}>
                            <div
                                className={s.avaContent}
                                dangerouslySetInnerHTML={{
                                    __html: getHtmlContent(inboxMessage.avaReply.body, inboxMessage.senderType === EMessageSenderType.PROVIDER),
                                }}
                            />
                            <div className={s.avaIcon}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M6.3019 11.292C8.92776 11.292 11.0564 9.16336 11.0564 6.5375C11.0564 3.91164 8.92776 1.78296 6.3019 1.78296C3.67604 1.78296 1.54736 3.91164 1.54736 6.5375C1.54736 9.16336 3.67604 11.292 6.3019 11.292ZM8.5051 5.76911C8.73719 5.53701 8.73719 5.16071 8.5051 4.92862C8.273 4.69652 7.8967 4.69652 7.66461 4.92862L5.70758 6.88564L4.9392 6.11725C4.7071 5.88516 4.3308 5.88516 4.0987 6.11725C3.86661 6.34935 3.86661 6.72565 4.0987 6.95774L5.28734 8.14638C5.51943 8.37847 5.89573 8.37847 6.12783 8.14638L8.5051 5.76911Z" fill="#069D70" />
                                </svg>
                                Replied by
                                <img width={26} src={AvaIcon} />
                            </div>
                        </div>

                    </div>
                )
            }
            {
                inboxMessage?.messageType === EMessageType.UNREAD_MARK && (
                    <div className={s.unreadWrap}>
                        <span className={s.unreadText}>
                            Unread messages
                        </span>
                    </div>
                )
            }
            {
                inboxMessage?.email && (
                    <div className={s.emailItem}>
                        <div className={s.sendTimeWrap}>
                            {
                                showName ? `${firstName} ${lastName}`.trim() : ''
                            }
                            <span className={s.sendTimeVal}>
                                {getTimeStr(dayjs(inboxMessage.createdAt), 'hh:mm A')}
                            </span>
                        </div>
                        <div className={s.emailWrap}>
                            <div className={s.subject}>{inboxMessage.email.subject}</div>
                            <div
                                className={s.content}
                                dangerouslySetInnerHTML={{
                                    __html: getHtmlContent(inboxMessage.email.body, inboxMessage.senderType === EMessageSenderType.PROVIDER),
                                }}
                            />
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default InboxMessage;
